import { Paperclip, Download } from 'lucide-react';
import { Modal } from '../../../../components/common/Modal';
import { useLanguage } from '../../../../features/language/useLanguage';

interface PaymentProofPreviewModalProps {
  isOpen: boolean;
  proofPath: string | null;
  blobUrl: string | null;
  isImage: (path: string | null | undefined) => boolean;
  onClose: () => void;
  onDownload: (path: string) => void;
}

/** Modal previewing a payment proof (image inline, otherwise a download prompt). */
export function PaymentProofPreviewModal({
  isOpen,
  proofPath,
  blobUrl,
  isImage,
  onClose,
  onDownload,
}: PaymentProofPreviewModalProps) {
  const { t } = useLanguage();
  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('purchaseOrder.paymentDetails.proofOfPayment')} maxWidth="4xl">
      <div className="flex flex-col items-center justify-center p-2 bg-gray-50 rounded-lg min-h-[50vh]">
        {isImage(proofPath) ? (
          blobUrl ? (
            <img src={blobUrl} alt="Payment Proof Full" className="max-w-full max-h-[70vh] object-contain rounded-md shadow-lg" />
          ) : (
            <div className="flex flex-col items-center gap-2 text-gray-400">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              <span className="text-sm italic">{t('inventory.lotDetail.loadingShort')}</span>
            </div>
          )
        ) : (
          <div className="flex flex-col items-center gap-4 py-8">
            <Paperclip className="h-16 w-16 text-gray-300" />
            <p className="text-gray-500 font-medium">{proofPath?.split('/').pop()}</p>
            <button
              type="button"
              onClick={() => onDownload(proofPath!)}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-primary-600 text-white rounded-md hover:bg-primary-700 shadow-sm ring-1 ring-inset ring-primary-700/20 transition-colors"
            >
              <Download className="h-4 w-4" />
              {t('purchaseOrder.actions.downloadProof')}
            </button>
          </div>
        )}
      </div>

      {isImage(proofPath) && (
        <div className="mt-4 flex justify-between items-center px-2">
          <span className="text-xs text-gray-500 font-mono truncate max-w-[70%]">{proofPath?.split('/').pop()}</span>
          <button
            type="button"
            onClick={() => onDownload(proofPath!)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-primary-700 bg-white rounded-md hover:bg-gray-50 ring-1 ring-inset ring-gray-300/70 transition-colors"
          >
            <Download className="h-4 w-4" />
            {t('purchaseOrder.actions.downloadProof')}
          </button>
        </div>
      )}
    </Modal>
  );
}
